import { useState, useEffect, useCallback } from 'react';
import { Stack, Box } from '@mui/material';
import dayjs from 'dayjs';

import { useVehicleStatusDashboardDynamic } from '../../../stores/vehicleStatusStore';
import { useMasterVehicleList } from '../../../stores/masterVehicleStore';
// import { useLanguage } from '../../../utils/i18n';
import useVehicleNotifications from '../../../hooks/useVehicleNotifications';
import { usePolygonTypeList } from '../../../stores/polygonTypeStore';
import { fetchRetase, fetchRetaseDetail, dashboardCache } from '../../../stores/retaseStore';
import { timezone } from '../../../utils/timeZone';

import VehicleLocation from './VehicleLocation';
import VehicleRitase from './VehicleRitase';
import TopGeolocations from './TopGeolocation';
import PeriodeRange from './periodik/PeriodeRange';
import RevenueEvVehicle from './RevenueEvVehicle';

const DashboardRevenue = () => {
  const [locationData, setLocationData] = useState({ pit: 0, eto: 0, efo: 0, jetty: 0, dynamicGeoCounts: {} });
  const [ritaseData, setRitaseData] = useState({ totalVehicles: 0, totalRitase: 0, avgRitasePerDay: 0, rawTrips: [] });
  const [inGeolocation, setInGeolocation] = useState(0);
  const [inGeoVehicles, setInGeoVehicles] = useState([]);
  const [topPools, setTopPools] = useState([]);
  const [loadingRitase, setLoadingRitase] = useState(true);

  const { data: polygonTypeData } = usePolygonTypeList(0, 1000, 'name', '');
  // const { t } = useLanguage();

  const { data: vehicleList, isLoading: isMasterLoading } = useMasterVehicleList();
  const { data: statusData, isLoading: isStatusLoading } = useVehicleStatusDashboardDynamic(100);

  // Hook for vehicle notifications
  useVehicleNotifications(statusData);

  const calculateVehicleStatus = useCallback(() => {
    if (!statusData || !Array.isArray(statusData)) return;

    const pit = { total: 0, atLocation: 0, delay: 0, vehicles: [] };
    const jetty = { total: 0, atLocation: 0, delay: 0, vehicles: [] };
    let inGeo = 0;
    const inGeoVehicles = [];
    const poolCounts = {};
    const dynamicGeoCounts = {};

    statusData.forEach((v) => {
      const statusRaw = String(v.status || v.position || v.location || '').toLowerCase();
      const spatialTypeRaw = String(v?.spatial_type || v?.raw?.spatial_type || '').toLowerCase();
      const spatialDescRaw = String(v?.raw?.polygon_type_name || v?.polygon_type_name || '').toLowerCase();

      const poolName = v.location || 'Unknown';
      let countedGeo = false;

      const isDelay = statusRaw.includes('delay');
      const isAtPool = statusRaw.includes('at pool') || (isDelay && spatialTypeRaw.includes('pool'));
      const isAtCustomer = statusRaw.includes('at customer') || (isDelay && spatialTypeRaw.includes('customer'));
      const isAtGeofence = statusRaw.includes('at geofence') || (isDelay && spatialTypeRaw.includes('geofence'));

      if (isAtPool) {
        pit.total++;
        if (isDelay) pit.delay++;
        else pit.atLocation++;
        pit.vehicles.push(v);
        countedGeo = true;
      } else if (isAtCustomer) {
        jetty.total++;
        if (isDelay) jetty.delay++;
        else jetty.atLocation++;
        jetty.vehicles.push(v);
        countedGeo = true;
      } else if (isAtGeofence) {
        if (polygonTypeData && Array.isArray(polygonTypeData)) {
          for (const pt of polygonTypeData) {
            if (spatialDescRaw.includes(String(pt.name).toLowerCase())) {
              if (!dynamicGeoCounts[pt.name]) {
                dynamicGeoCounts[pt.name] = { total: 0, atLocation: 0, delay: 0, vehicles: [] };
              }
              dynamicGeoCounts[pt.name].total++;
              if (isDelay) dynamicGeoCounts[pt.name].delay++;
              else dynamicGeoCounts[pt.name].atLocation++;
              dynamicGeoCounts[pt.name].vehicles.push(v);
              countedGeo = true;
              break;
            }
          }
        }
      }

      if (countedGeo) {
        inGeo++;
        inGeoVehicles.push(v);
        if (poolName && poolName !== 'Unknown') {
          if (!poolCounts[poolName]) {
            poolCounts[poolName] = { count: 0, vehicles: [] };
          }
          poolCounts[poolName].count++;
          poolCounts[poolName].vehicles.push(v);
        }
      }
    });

    setLocationData({ pit, jetty, dynamicGeoCounts });
    setInGeolocation(inGeo);
    setInGeoVehicles(inGeoVehicles);

    const top = Object.entries(poolCounts)
      .map(([pool, val]) => ({ pool, jumlah: val.count, vehicles: val.vehicles }))
      .sort((a, b) => b.jumlah - a.jumlah)
      .slice(0, 5);

    setTopPools(top);
  }, [statusData, polygonTypeData]);

  useEffect(() => {
    calculateVehicleStatus();
  }, [calculateVehicleStatus]);

  const fetchRitaseData = useCallback(
    async (signal, forceRefresh = false) => {
      if (!Array.isArray(vehicleList) || vehicleList.length === 0) {
        return { totalVehicles: 0, totalRitase: 0, avgRitasePerDay: 0, rawTrips: [] };
      }

      const companyId = localStorage.getItem('companyId') || '';
      const startIso = dayjs().startOf('day').format('YYYY-MM-DDTHH:mm:ss');
      const endIso = dayjs().format('YYYY-MM-DDTHH:mm:ss');

      const cacheKey = `revenue_${companyId}_today`;
      const cachedData = dashboardCache.get(cacheKey);
      if (!forceRefresh && cachedData) {
        return cachedData;
      }

      const promises = vehicleList.map(async (v) => {
        const vId = v.id || v.vehicle_id;
        if (!vId) return null;
        const vCompanyId = v.company_id ?? v.company ?? companyId;

        try {
          let dates = [];
          let curr = dayjs(startIso).startOf('day');
          const end = dayjs(endIso).startOf('day');
          while (curr.isBefore(end) || curr.isSame(end, 'day')) {
            dates.push(curr);
            curr = curr.add(1, 'day');
          }

          const dailyPromises = dates.map(async (dateObj) => {
            let dStartIso = dateObj.startOf('day');
            let dEndIso = dateObj.endOf('day');

            if (dateObj.isSame(dayjs(startIso), 'day')) dStartIso = dayjs(startIso);
            if (dateObj.isSame(dayjs(endIso), 'day')) dEndIso = dayjs(endIso);

            const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
            const finalStartIso = dStartIso.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:00[Z]');
            const finalEndIso = dEndIso.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:59[Z]');

            try {
              const resDetail = await fetchRetase({
                vehicleId: String(vId),
                companyId: String(vCompanyId),
                startTime: finalStartIso,
                endTime: finalEndIso,
                tz,
                signal
              });
              const apiDataRaw = resDetail?.data?.retase ?? [];

              const uniqueTripsMap = new Map();
              apiDataRaw.forEach((tripData) => {
                const routes = tripData.routes || [];
                const details = tripData.details || [];

                const spatialNames = routes.map((v) => v.spatial_name || v.spatialName).filter(Boolean);
                const routeTitle = spatialNames.length > 0 ? spatialNames.join(' - ') : `Route ID: ${tripData.route_trip_id}`;

                let earliestStart = null;
                let latestEnd = null;
                details.forEach((d) => {
                  const st = dayjs(d.start);
                  const en = dayjs(d.end);
                  if (!earliestStart || st.isBefore(earliestStart)) earliestStart = st;
                  if (!latestEnd || en.isAfter(latestEnd)) latestEnd = en;
                });

                const finalStart = earliestStart ? earliestStart.format('YYYY-MM-DD HH:mm:ss') : 'no-start';
                const finalEnd = latestEnd ? latestEnd.format('YYYY-MM-DD HH:mm:ss') : 'no-end';

                const uniqueKey = `${routeTitle}_${finalStart}_${finalEnd}`;
                if (!uniqueTripsMap.has(uniqueKey)) {
                  uniqueTripsMap.set(uniqueKey, tripData);
                }
              });
              const apiData = Array.from(uniqueTripsMap.values());

              if (!Array.isArray(apiData) || apiData.length === 0) return [];

              const processedTrips = await Promise.all(
                apiData.map(async (tripData) => {
                  const routes = tripData.routes || [];
                  const details = tripData.details || [];

                  if (details.length === 0) {
                    return {
                      vehicle_id: vId,
                      route_trip_id: tripData.route_trip_id,
                      in_time: tripData.start || tripData.in_time || 'no-start',
                      out_time: tripData.end || tripData.out_time || 'no-end',
                      duration: tripData.dur || 0,
                      visits: routes,
                      details: [],
                      ritase_count: tripData.total_trips || 1
                    };
                  }

                  const detailPromises = details.map(async (d, dIdx) => {
                    const st = dayjs(d.start);
                    const en = dayjs(d.end);
                    const finalStartIso = st.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
                    const finalEndIso = en.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

                    let nextRouteTimeIso = undefined;
                    if (details.length > 1 && dIdx < details.length - 1) {
                      const nextSt = dayjs(details[dIdx + 1].start);
                      nextRouteTimeIso = nextSt.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
                    }

                    const finalStart = st.format('YYYY-MM-DD HH:mm:ss');
                    const finalEnd = en.format('YYYY-MM-DD HH:mm:ss');

                    try {
                      const detailRes = await fetchRetaseDetail({
                        companyId: String(vCompanyId),
                        vehicleId: String(vId),
                        routeTripId: String(tripData.route_trip_id),
                        startTime: finalStartIso,
                        endTime: finalEndIso,
                        nextRouteTime: nextRouteTimeIso,
                        tz,
                        page: 0,
                        rowsPerPage: 10000,
                        signal
                      });

                      const deepDetailLogs = detailRes?.data?.data || detailRes?.data || [];
                      return {
                        ...d,
                        in_time: finalStart,
                        out_time: finalEnd,
                        deepDetails: deepDetailLogs
                      };
                    } catch (detailErr) {
                      if (detailErr.name !== 'CanceledError' && detailErr.code !== 'ERR_CANCELED') {
                        console.error(detailErr);
                      }
                      return {
                        ...d,
                        in_time: finalStart,
                        out_time: finalEnd,
                        deepDetails: []
                      };
                    }
                  });

                  const resolvedDetails = await Promise.all(detailPromises);

                  return {
                    vehicle_id: vId,
                    route_trip_id: tripData.route_trip_id,
                    in_time: resolvedDetails[0]?.in_time || tripData.start || tripData.in_time,
                    out_time: resolvedDetails[resolvedDetails.length - 1]?.out_time || tripData.end || tripData.out_time,
                    duration: tripData.dur || 0,
                    visits: routes,
                    details: resolvedDetails,
                    ritase_count: resolvedDetails.length || tripData.total_trips || 1
                  };
                })
              );

              return processedTrips;
            } catch {
              return [];
            }
          });

          const dailyResults = await Promise.all(dailyPromises);
          const combinedTrips = dailyResults.flat();

          const vehicleTotalRitase = combinedTrips.reduce((acc, curr) => acc + (curr ? curr.ritase_count || 0 : 0), 0);

          return {
            vehicleId: vId,
            tripCount: vehicleTotalRitase,
            trips: combinedTrips
          };
        } catch (err) {
          if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
            console.error(`Gagal fetch ritase 24h untuk vehicle ${vId}`, err);
          }
          return null;
        }
      });

      const results = await Promise.all(promises);

      let totalRitaseCount = 0;
      const activeVehicles = new Set();
      let allTripsCombined = [];

      results.forEach((res) => {
        if (res && res.tripCount > 0) {
          activeVehicles.add(res.vehicleId);
          totalRitaseCount += res.tripCount;
          allTripsCombined = allTripsCombined.concat(res.trips);
        }
      });

      const finalResult = {
        totalVehicles: activeVehicles.size,
        totalRitase: totalRitaseCount,
        avgRitasePerDay: activeVehicles.size > 0 ? (totalRitaseCount / activeVehicles.size).toFixed(1) : 0,
        rawTrips: allTripsCombined
      };

      dashboardCache.set(cacheKey, finalResult);
      return finalResult;
    },
    [vehicleList]
  );

  useEffect(() => {
    if (isMasterLoading) return;

    let isComponentMounted = true;
    const controller = new AbortController();

    const executeBackgroundFetch = async (isFirstLoad = false) => {
      setLoadingRitase(true);
      try {
        // Pass forceRefresh = !isFirstLoad so that the 10-minute interval actually updates the cache
        const fetchedData = await fetchRitaseData(controller.signal, !isFirstLoad);
        if (isComponentMounted && fetchedData) {
          setRitaseData(fetchedData);
        }
      } catch (err) {
        if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
          console.error('Fatal Error saat Execute Background Fetch', err);
        }
      } finally {
        if (isComponentMounted) setLoadingRitase(false);
      }
    };

    executeBackgroundFetch(true);

    const intervalId = setInterval(() => {
      executeBackgroundFetch(false);
    }, 600000); // Hit per 10 menit

    return () => {
      isComponentMounted = false;
      controller.abort();
      clearInterval(intervalId);
    };
  }, [isMasterLoading, fetchRitaseData]);

  const handleRefreshGeolocations = () => {
    calculateVehicleStatus();
  };

  const handleRefreshRitase = async () => {
    setLoadingRitase(true);
    try {
      const fetchedData = await fetchRitaseData(undefined, true);
      if (fetchedData) {
        setRitaseData(fetchedData);
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        console.error('Fatal Error saat Manual Refresh', err);
      }
    } finally {
      setLoadingRitase(false);
    }
  };

  // --- Helper conditional rendering widgets ---
  const appSettingStr = localStorage.getItem('appSetting');
  const appSetting = appSettingStr ? JSON.parse(appSettingStr) : {};
  const widgetsConfig = appSetting?.widgets || null;
  const showWidget = (key) => {
    if (!widgetsConfig) return true; // default true if null/undefined
    if (!widgetsConfig.revenue) return true;
    return widgetsConfig.revenue[key] !== false;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack spacing={2} mt={2}>
        {showWidget('vehicle-location') && <VehicleLocation data={locationData} polygonTypes={polygonTypeData} loading={isStatusLoading} />}

        {showWidget('vehicle-retase') && <VehicleRitase data={ritaseData} loading={loadingRitase} onRefresh={handleRefreshRitase} />}

        {showWidget('vehicle-in-geolocation') && (
          <TopGeolocations
            inGeolocation={inGeolocation}
            inGeoVehicles={inGeoVehicles}
            topPools={topPools}
            onRefresh={handleRefreshGeolocations}
            loading={isStatusLoading}
          />
        )}
        {showWidget('electric-vehicle') && <RevenueEvVehicle />}
        <PeriodeRange />
      </Stack>
    </Box>
  );
};

export default DashboardRevenue;
